import React, { useState, useEffect } from "react";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useDashboardUI } from "../../context/GlobalContext";
import incidentService from "../../services/incidentService";
import chatService from "../../services/chatService";
import { toast } from "react-toastify";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  X,
  TriangleAlert,
  Check,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Building2,
  User,
  Users,
  Siren,
  ThumbsDown,
  ThumbsUp,
  Info,
} from "lucide-react";
import { displayFacilityNameByFacilityNumber } from "../../helpers/helpers";

const PRIORITY_CONFIG = {
  low:      { label: "Low",      cls: "bg-green-100 text-green-700 border-green-200"   },
  medium:   { label: "Medium",   cls: "bg-amber-50 text-amber-700 border-amber-200"    },
  high:     { label: "High",     cls: "bg-orange-100 text-orange-700 border-orange-200" },
  critical: { label: "Critical", cls: "bg-red-100 text-red-700 border-red-200"         },
};

const Field = ({ label, required, error, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const inputCls = (hasError) =>
  `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B3674]/20 focus:border-[#2B3674] transition-colors ${
    hasError ? "border-red-400 bg-red-50" : "border-gray-200"
  }`;

// Message Bubble
const MessageBubble = ({ msg, isTarget }) => (
  <div>
    <p className="text-xs text-gray-400 mb-0.5 font-semibold opacity-60">
      {msg.role === "assistant" ? "Assistant" : "User"} · {new Date(msg.created_at).toLocaleString()}
    </p>
    <div className={`text-xs px-2.5 py-2 rounded-lg whitespace-pre-wrap leading-relaxed ${
      msg.role === "user"
        ? "bg-blue-50 text-blue-900 border border-blue-100"
        : "bg-green-50 text-green-900 border border-green-100"
    } ${isTarget ? "ring-2 ring-amber-300" : ""}`}>
      {msg.content || "No content"}

      {/* Attachment indicators */}
      {(msg.has_generated_image || msg.image_paths?.length > 0 || msg.generated_files || msg.related_files) && (
        <div className="flex flex-wrap gap-1 mt-1.5 pt-1.5 border-t border-black/5">
          {(msg.has_generated_image || msg.image_paths?.length > 0) && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/5 text-current opacity-70">
              🖼 {msg.image_paths?.length > 1 ? `${msg.image_paths.length} images` : "Image"}
            </span>
          )}
          {msg.generated_files && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/5 text-current opacity-70">
              📄 Generated file
            </span>
          )}
          {msg.related_files && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/5 text-current opacity-70">
              📎 Attached file
            </span>
          )}
        </div>
      )}
    </div>
  </div>
);

// Linked Feedback Panel
const LinkedFeedbackPanel = ({ items, loading }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5">
        <RefreshCcw className="h-3.5 w-3.5 animate-spin" />
        Loading linked feedback…
      </div>
    );
  }

  if (!items.length) return null;

  const toggleItem = (id) => setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));

  // Show accurate per-type counts rather than collapsing to one type
  const downCount = items.filter(i => i.feedback_type === "thumbs_down").length;
  const upCount   = items.filter(i => i.feedback_type === "thumbs_up").length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Summary trigger */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5 text-[#2B3674]" />
          <span className="text-xs font-semibold text-gray-700">
            {items.length} feedback item{items.length > 1 ? "s" : ""} linked
          </span>
          <span className="flex gap-1">
            {downCount > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium border bg-red-100 text-red-700 border-red-200">
                <ThumbsDown className="h-2.5 w-2.5" />
                {downCount > 1 && <span className="ml-0.5">{downCount}</span>}
              </span>
            )}
            {upCount > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium border bg-green-100 text-green-700 border-green-200">
                <ThumbsUp className="h-2.5 w-2.5" />
                {upCount > 1 && <span className="ml-0.5">{upCount}</span>}
              </span>
            )}
          </span>
        </div>
        {expanded
          ? <ChevronUp className="h-4 w-4 text-gray-400" />
          : <ChevronDown className="h-4 w-4 text-gray-400" />
        }
      </button>

      {/* Expanded items */}
      {expanded && (
        <div className="divide-y divide-gray-100">
          {items.map((item, index) => (
            <div key={item.id} className="px-3 py-3 bg-white">
              {/* Item header */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-gray-400 tabular-nums">#{index + 1}</span>
                <span className="text-sm font-medium text-gray-900">
                  {item.user_name?.trim() || "Anonymous"}
                </span>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium border ${
                  item.feedback_type === "thumbs_up"
                    ? "bg-green-100 text-green-700 border-green-200"
                    : "bg-red-100 text-red-700 border-red-200"
                }`}>
                  {item.feedback_type === "thumbs_up"
                    ? <ThumbsUp className="h-2.5 w-2.5" />
                    : <ThumbsDown className="h-2.5 w-2.5" />
                  }
                  {item.feedback_type === "thumbs_up" ? "Positive" : "Negative"}
                </span>
                {item.category && (
                  <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                    {item.category.replace(/_/g, " ")}
                  </span>
                )}
              </div>

              {/* Comment */}
              {item.comment && (
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 border-l-2 border-[#2B3674]/30 leading-relaxed mb-2">
                  "{item.comment}"
                </p>
              )}

              {/* Chat context — expandable */}
              {item.chatContext?.messages?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleItem(item.id)}
                      className="flex items-center gap-1.5 text-xs text-[#2B3674] hover:underline cursor-pointer"
                    >
                      <MessageSquare className="h-3 w-3" />
                      {expandedItems[item.id] ? "Hide" : "Show"} conversation context ({item.chatContext.messages.length} messages)
                    </button>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default shrink-0">
                            <Info className="h-3 w-3 text-gray-300 hover:text-gray-500 transition-colors" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          align="start"
                          className="max-w-xs bg-gray-900 text-white text-xs rounded-lg px-3 py-2 leading-relaxed shadow-xl border-0"
                        >
                          The conversation context helps you understand the full situation before taking action.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  {expandedItems[item.id] && (
                    <div className="bg-gray-50 rounded-lg border border-gray-100 p-2 space-y-2">
                      {item.chatContext.messages.map((msg, i) => (
                        <MessageBubble
                          key={msg.id || i}
                          msg={msg}
                          isTarget={item.chatContext.target_message_id === msg.id}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main component
const CreateIncidentModal = () => {
  const {
    showCreateIncidentModal,
    closeCreateIncidentModal,
    selectedFeedback,
    feedbackList,
    isSuperAdmin,
    triggerRefresh,
  } = useFeedback();

  const { facilityList } = useDashboardUI();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    facility_id: null,
    priority: "medium",
    assignment_type: "facility_admin",
    assigned_user_id: null,
    feedback_ids: [],
  });

  // Only superadmins can create incidents
  const steps = ["Details", "Facility & Assignment", "Review"];
  const totalSteps = steps.length;

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [errors, setErrors] = useState({});
  const [facilityUsers, setFacilityUsers] = useState([]);
  const [selectedFeedbackItems, setSelectedFeedbackItems] = useState([]);

  // Reset on open
  useEffect(() => {
    if (showCreateIncidentModal) {
      setFormData({
        title: "", description: "", facility_id: null,
        priority: "medium", assignment_type: "facility_admin",
        assigned_user_id: null, feedback_ids: selectedFeedback || [],
      });
      setFacilityUsers([]);
      setCurrentStep(1);
      setErrors({});
      fetchSelectedFeedbackDetails();
    }
  }, [showCreateIncidentModal, selectedFeedback]);

  const fetchSelectedFeedbackDetails = async () => {
    if (!selectedFeedback?.length) return;
    setIsLoadingFeedback(true);
    try {
      // Use items already in feedbackList
      const items = feedbackList.filter((i) => selectedFeedback.includes(i.id));

      // Enrich with chat context where available
      const enhanced = await Promise.all(items.map(async (item) => {
        let chatContext = null;
        if (item.chat_id) {
          try { chatContext = await chatService.getConversationContext(item.chat_id, item.message_id, 5); }
          catch {}
        }
        return { ...item, chatContext };
      }));
      setSelectedFeedbackItems(enhanced);
    } catch {
      setSelectedFeedbackItems([]);
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const fetchFacilityUsers = async (facilityId) => {
    if (!facilityId) return;
    try {
      const result = await incidentService.getFacilityUsers(facilityId);
      setFacilityUsers(result.users || []);
    } catch {
      toast.error("Failed to load facility users");
      setFacilityUsers([]);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    // Superadmins: re-fetch users when facility changes; clear stale list first
    if (field === "facility_id") { setFacilityUsers([]); fetchFacilityUsers(value); }
    if (field === "assignment_type" && value === "direct_assignment" && formData.facility_id) fetchFacilityUsers(formData.facility_id);
  };

  // Validation rules per step
  const validateStep = (step) => {
    const errs = {};
    if (step === 1) {
      if (!formData.title.trim()) errs.title = "Title is required";
      else if (formData.title.length > 200) errs.title = "Max 200 characters";
      if (formData.description.length > 2000) errs.description = "Max 2000 characters";
      if (!formData.priority) errs.priority = "Priority is required";
    }
    if (step === 2) {
      if (!formData.facility_id) errs.facility_id = "Please select a facility";
      if (formData.assignment_type === "direct_assignment" && !formData.assigned_user_id)
        errs.assigned_user_id = "Please select a user";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => { if (validateStep(currentStep)) setCurrentStep((p) => Math.min(p + 1, totalSteps)); };
  const handlePrev = () => setCurrentStep((p) => Math.max(p - 1, 1));

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await incidentService.createIncident(formData);
      toast.success("Incident created successfully");
      triggerRefresh();
      closeCreateIncidentModal();
    } catch { toast.error("Failed to create incident"); }
    finally { setIsLoading(false); }
  };

  const getAssignedUserName = () => {
    if (!formData.assigned_user_id) return null;
    const u = facilityUsers.find((x) => x.id === formData.assigned_user_id);
    return u?.name?.trim() || "Unknown User";
  };

  const AssignmentBlock = () => (
    <Field label="Assignment" required error={errors.assignment_type}>
      <div className="space-y-2">
        {[
          {
            value: "facility_admin",
            title: "Facility Admin",
            desc: "Assigns to the facility administrator — recommended for general issues.",
            icon: Users,
          },
          {
            value: "direct_assignment",
            title: "Direct Assignment",
            desc: "Assign directly to a specific user in the facility.",
            icon: User,
          },
        ].map(({ value, title, desc, icon: Icon }) => (
          <div
            key={value}
            onClick={() => handleChange("assignment_type", value)}
            className={`flex items-start gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-colors ${
              formData.assignment_type === value
                ? "border-[#2B3674] bg-[#2B3674]/5"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
              formData.assignment_type === value
                ? "border-[#2B3674] bg-[#2B3674]"
                : "border-gray-300"
            }`}>
              {formData.assignment_type === value && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-sm font-medium text-gray-900">{title}</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {formData.assignment_type === "direct_assignment" && (
        <div className="mt-3">
          <Field label="Assign to User" required error={errors.assigned_user_id}>
            <Select
              value={formData.assigned_user_id || ""}
              onValueChange={(v) => handleChange("assigned_user_id", v)}
            >
              <SelectTrigger className={`h-9 text-sm hover:border-gray-300 hover:bg-gray-50 transition-colors focus:ring-1 focus:ring-[#2B3674]/20 focus:border-[#2B3674] ${errors.assigned_user_id ? "border-red-400" : "border-gray-200"}`}>
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg p-1">
                {facilityUsers.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-gray-400 text-center">No users found</div>
                ) : facilityUsers.map((user) => (
                  <SelectItem key={user.id} value={user.id} className="text-sm rounded-md cursor-pointer hover:bg-gray-100 focus:bg-gray-100 outline-none">
                    <div>
                      <span className="font-medium text-gray-900">{user.name}</span>
                      {user.email && <span className="text-gray-400 ml-1.5 text-xs">{user.email}</span>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      )}
    </Field>
  );

  // Steps
  const renderStep = () => {
    // Superadmin: step 1 = details only, step 2 = facility + assignment
    // Facility admin: step 1 = details + assignment
    // Both: last step = review

    if (currentStep === totalSteps) {
      // Review step
      const priority = PRIORITY_CONFIG[formData.priority];
      return (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Incident Summary</p>

            {[
              { label: "Title",       value: formData.title },
              { label: "Description", value: formData.description || null },
            ].map(({ label, value }) => value ? (
              <div key={label} className="flex gap-3">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide min-w-24 pt-0.5">{label}</span>
                <span className="text-sm text-gray-700 leading-relaxed">{value}</span>
              </div>
            ) : null)}

            <div className="flex gap-3 items-center">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide min-w-24">Priority</span>
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${priority.cls}`}>
                {priority.label}
              </span>
            </div>

            {formData.facility_id && (
              <div className="flex gap-3 items-center">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide min-w-24">Facility</span>
                <div className="flex items-center gap-1.5 text-sm text-gray-700">
                  <Building2 className="h-3.5 w-3.5 text-gray-400" />
                  {displayFacilityNameByFacilityNumber(facilityList, formData.facility_id)}
                </div>
              </div>
            )}

            <div className="flex gap-3 items-center">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide min-w-24">Assignment</span>
              <div className="flex items-center gap-1.5 text-sm text-gray-700">
                {formData.assignment_type === "facility_admin"
                  ? <><Users className="h-3.5 w-3.5 text-gray-400" />Facility Administrator</>
                  : <><User className="h-3.5 w-3.5 text-gray-400" />{getAssignedUserName() || "Direct Assignment"}</>
                }
              </div>
            </div>

            {selectedFeedbackItems.length > 0 && (
              <div className="flex gap-3 items-center">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide min-w-24">Linked</span>
                <span className="text-sm text-gray-700">{selectedFeedbackItems.length} feedback item{selectedFeedbackItems.length > 1 ? "s" : ""}</span>
              </div>
            )}
          </div>

          <LinkedFeedbackPanel items={selectedFeedbackItems} loading={isLoadingFeedback} />
        </div>
      );
    }

    // Step 1 (both admin types) — always includes details
    if (currentStep === 1) {
      return (
        <div className="space-y-4">
          <Field label="Title" required error={errors.title}>
            <div className="relative">
              <input
                placeholder="Enter a descriptive title for this incident"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                maxLength={200}
                className={inputCls(!!errors.title)}
              />
              <span className="absolute right-3 top-2 text-xs text-gray-300 pointer-events-none">
                {formData.title.length}/200
              </span>
            </div>
          </Field>

          <Field label="Description" error={errors.description}>
            <div className="relative">
              <textarea
                placeholder="Provide additional context about the issue (optional)"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                maxLength={2000}
                rows={3}
                className={inputCls(!!errors.description) + " resize-none"}
              />
              <span className="absolute right-3 bottom-2 text-xs text-gray-300 pointer-events-none">
                {formData.description.length}/2000
              </span>
            </div>
          </Field>

          <Field label="Priority" required error={errors.priority}>
            <Select value={formData.priority} onValueChange={(v) => handleChange("priority", v)}>
              <SelectTrigger className={`h-9 text-sm hover:border-gray-300 hover:bg-gray-50 transition-colors focus:ring-1 focus:ring-[#2B3674]/20 focus:border-[#2B3674] ${errors.priority ? "border-red-400" : "border-gray-200"}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg p-1">
                {Object.entries(PRIORITY_CONFIG).map(([value, { label, cls }]) => (
                  <SelectItem key={value} value={value} className="text-sm rounded-md cursor-pointer hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>{label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Linked feedback summary */}
          {(selectedFeedbackItems.length > 0 || isLoadingFeedback) && (
            <LinkedFeedbackPanel items={selectedFeedbackItems} loading={isLoadingFeedback} />
          )}
        </div>
      );
    }

    // Step 2 — facility selection + assignment (superadmin only flow)
    if (currentStep === 2) {
      return (
        <div className="space-y-4">
          <Field label="Target Facility" required error={errors.facility_id}>
            <Select
              value={formData.facility_id?.toString() || ""}
              onValueChange={(v) => handleChange("facility_id", parseInt(v))}
            >
              <SelectTrigger className={`h-9 text-sm hover:border-gray-300 hover:bg-gray-50 transition-colors focus:ring-1 focus:ring-[#2B3674]/20 focus:border-[#2B3674] ${errors.facility_id ? "border-red-400" : "border-gray-200"}`}>
                <SelectValue placeholder="Select facility" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg p-1">
                {facilityList.map((f) => (
                  <SelectItem key={f.facility_number} value={f.facility_number.toString()} className="text-sm rounded-md cursor-pointer hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-gray-400" />
                      {f.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <AssignmentBlock />
        </div>
      );
    }

    return null;
  };

  if (!showCreateIncidentModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-xl m-4">
        <Card className="shadow-2xl bg-white rounded-2xl overflow-hidden">
          <div className="p-6">

            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Siren className="h-5 w-5 text-[#2B3674]" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Create Incident</h2>
                  <p className="text-xs text-gray-400">Step {currentStep} of {totalSteps} — {steps[currentStep - 1]}</p>
                </div>
              </div>
              <Button
                variant="ghost" size="sm"
                onClick={closeCreateIncidentModal}
                className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Step progress */}
            <div className="flex items-center gap-0 mb-6">
              {steps.map((label, i) => {
                const stepNum = i + 1;
                const isDone = stepNum < currentStep;
                const isActive = stepNum === currentStep;
                return (
                  <React.Fragment key={stepNum}>
                    <div className="flex flex-col items-center gap-1">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                        isDone    ? "bg-green-500 text-white"
                        : isActive ? "bg-[#2B3674] text-white"
                        :            "bg-gray-100 text-gray-400"
                      }`}>
                        {isDone ? <Check className="h-3.5 w-3.5" /> : stepNum}
                      </div>
                      <span className={`text-xs whitespace-nowrap ${isActive ? "text-[#2B3674] font-medium" : "text-gray-400"}`}>
                        {label}
                      </span>
                    </div>
                    {i < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 mb-4 transition-colors ${stepNum < currentStep ? "bg-green-400" : "bg-gray-100"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Content */}
            <div className="max-h-[50vh] overflow-y-auto pr-1">
              {renderStep()}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
              <Button
                onClick={handlePrev}
                variant="outline"
                disabled={currentStep === 1}
                className="gap-1.5 text-gray-600 hover:bg-gray-100 hover:border-gray-300 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  onClick={closeCreateIncidentModal}
                  variant="outline"
                  className="text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </Button>

                {currentStep === totalSteps ? (
                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="bg-[#2B3674] hover:bg-[#1E2951] text-white gap-1.5"
                  >
                    {isLoading
                      ? <><RefreshCcw className="h-4 w-4 animate-spin" />Creating…</>
                      : <><Check className="h-4 w-4" />Create Incident</>
                    }
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    className="bg-[#2B3674] hover:bg-[#1E2951] text-white gap-1.5"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CreateIncidentModal;